import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useSpring, animated } from 'react-spring';
import styles from './InteractiveImageGrid.module.css';

// Utility function to check if URL is a video
const isVideoUrl = (url) => {
  if (!url) return false;
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.wmv', '.flv', '.m4v'];
  const lowerUrl = url.toLowerCase();
  return videoExtensions.some(ext => lowerUrl.includes(ext));
};

// Per-tile component
function GridImageTile({
  image,
  index,
  focusedIndex,
  inView,
  onClick,
  setInViewAtIndex,
  cancelFocus,
}) {
  const ref = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Spring animation for container entrance
  const [containerSpring, containerApi] = useSpring(() => ({
    opacity: 1, // Start visible so intersection observer can detect them
    transform: 'scale(0.95)',
    config: { mass: 1, tension: 280, friction: 60 },
  }));

  // We removed the image and spinner springs since we're using simple state-based rendering now

  // State for lazy loading - MUST be declared before any code that uses it
  const [shouldLoadImage, setShouldLoadImage] = useState(false);
  
  // Check if this is a video
  const isVideo = isVideoUrl(image.url);

  // Only log for first few tiles to avoid spam - AFTER hooks are initialized
  if (index < 3) {
    console.log(`[TILE-${index}] Render - InView:`, inView, 'ShouldLoad:', shouldLoadImage, 'Loaded:', imageLoaded, 'Error:', imageError, 'IsVideo:', isVideo);
  }

  // Intersection Observer for lazy loading and animations
  useEffect(() => {
    if (!ref.current) {
      console.log(`[TILE-${index}] No ref.current available for intersection observer`);
      return;
    }
    console.log(`[TILE-${index}] Setting up intersection observer for lazy loading`);
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        console.log(`[TILE-${index}] Intersection observer triggered, isIntersecting:`, entry.isIntersecting);
        if (entry.isIntersecting && !shouldLoadImage) {
          console.log(`[TILE-${index}] Tile is in view, starting image load`);
          setInViewAtIndex(index);
          setShouldLoadImage(true); // Start loading the image
          containerApi.start({ opacity: 1, transform: 'scale(1)' });
          observer.disconnect(); // Stop observing once we've triggered loading
        }
      },
      { 
        threshold: 0.01, // Very low threshold to trigger early
        rootMargin: '200px' // Load images 200px before they come into view
      }
    );
    observer.observe(ref.current);
    return () => {
      console.log(`[TILE-${index}] Cleaning up intersection observer`);
      observer.disconnect();
    };
  }, [index, setInViewAtIndex, containerApi, shouldLoadImage]);

  const handleMediaLoad = () => {
    console.log(`[TILE-${index}] ${isVideo ? 'Video' : 'Image'} loaded successfully:`, image.url);
    console.log(`[TILE-${index}] Setting imageLoaded to true`);
    setImageLoaded(true);
    setImageError(false);
  };

  const handleMediaError = () => {
    console.error(`[TILE-${index}] ${isVideo ? 'Video' : 'Image'} failed to load:`, image.url);
    setImageError(true);
    setImageLoaded(true);
  };

  return (
    <animated.div
      key={image.id || index}
      ref={ref}
      className={styles.gridItem}
      style={{
        ...containerSpring,
        opacity: focusedIndex === index ? 0 : containerSpring.opacity,
      }}
      onClick={() => {
        if (focusedIndex === index && cancelFocus) {
          cancelFocus();
        } else {
          onClick(index);
        }
      }}
    >
      {/* Loading state - show when not in view or loading */}
      {!shouldLoadImage && (
        <div className={styles.loadingSpinner}>
          <div className={styles.placeholderText}>Loading...</div>
        </div>
      )}

      {/* Loading spinner - show when in view but image not loaded */}
      {shouldLoadImage && !imageLoaded && !imageError && (
        <div className={styles.loadingSpinner}>
          <div className={styles.spinner}></div>
        </div>
      )}
      
      {/* Error state */}
      {imageError && (
        <div className={styles.loadingSpinner}>
          <div className={styles.imageError}>
            <span>Failed to load</span>
          </div>
        </div>
      )}

      {/* Media - show when loaded */}
      {shouldLoadImage && !imageError && imageLoaded && (
        isVideo ? (
          <video 
            src={image.url}
            className={styles.gridImage}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: 2,
              opacity: 1,
            }}
            muted
            loop
            playsInline
            preload="metadata"
          />
        ) : (
          <img 
            src={image.url} 
            alt={`Uploaded by ${image.uploadedBy}`}
            className={styles.gridImage}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: 2,
              opacity: 1,
            }}
          />
        )
      )}
      
      {/* Hidden media for loading - only when we should load */}
      {shouldLoadImage && !imageError && !imageLoaded && (
        isVideo ? (
          <video 
            src={image.url}
            onLoadedData={handleMediaLoad}
            onError={handleMediaError}
            style={{
              position: 'absolute',
              opacity: 0,
              pointerEvents: 'none',
              width: '100%',
              height: '100%',
            }}
            muted
            preload="metadata"
          />
        ) : (
          <img 
            src={image.url} 
            alt={`Uploaded by ${image.uploadedBy}`}
            onLoad={handleMediaLoad}
            onError={handleMediaError}
            style={{
              position: 'absolute',
              opacity: 0,
              pointerEvents: 'none',
              width: '100%',
              height: '100%',
            }}
          />
        )
      )}

      {/* Video play icon overlay */}
      {isVideo && shouldLoadImage && imageLoaded && !imageError && (
        <div className={styles.videoPlayIcon}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="white">
            <path d="M8 5v14l11-7z"/>
          </svg>
        </div>
      )}

      {/* Uploader info - only show when media is loaded */}
      {imageLoaded && !imageError && (
        <div 
          className={styles.uploaderInfo}
        >
          {image.uploadedBy || 'Anonymous'}
        </div>
      )}
    </animated.div>
  );
}

const InteractiveImageGrid = ({ images, onFocusImage, focusedIndex, cancelFocus }) => {
  const gridRef = useRef(null);
  const [inViewArr, setInViewArr] = useState([]);

  console.log('[GRID] InteractiveImageGrid rendered with images:', images);
  console.log('[GRID] Images length:', images.length);

  useEffect(() => {
    console.log('[GRID] Setting inViewArr for', images.length, 'images');
    const newInViewArr = Array(images.length).fill(false);
    console.log('[GRID] New inViewArr:', newInViewArr);
    setInViewArr(newInViewArr);
  }, [images.length]);

  // Set inView for a specific index (for animation)
  const setInViewAtIndex = useCallback((idx) => {
    console.log('[GRID] Setting inView for index:', idx);
    setInViewArr(prev => {
      if (prev[idx]) return prev;
      const next = [...prev];
      next[idx] = true;
      console.log('[GRID] Updated inViewArr:', next);
      return next;
    });
  }, []);

  // Handle image click: send data to parent
  const handleImageClick = (index) => {
    // Focus logic unchanged
    const el = document.querySelectorAll('.' + styles.gridItem)[index];
    if (!el) return;
    const domRect = el.getBoundingClientRect();
    const spreadRect = {
      top: domRect.top,
      left: domRect.left,
      width: domRect.width,
      height: domRect.height,
      right: domRect.right,
      bottom: domRect.bottom,
      x: domRect.x,
      y: domRect.y,
    };
    const startTransform = 'scale(1) rotateX(0deg) rotateY(0deg) rotateZ(0deg)';
    const screenW = window.innerWidth;
    const screenH = window.innerHeight;
    const imgEl = el.querySelector('img');
    const aspect = (imgEl && imgEl.naturalWidth > 0) ? imgEl.naturalHeight / imgEl.naturalWidth : (spreadRect.height / spreadRect.width);
    const targetW = Math.min(screenW * 0.9, 800);
    const targetH = targetW * aspect;
    const endRect = {
      top: (screenH - targetH) / 2,
      left: (screenW - targetW) / 2,
      width: targetW,
      height: targetH,
    };
    if (onFocusImage) {
      onFocusImage({
        url: images[index].url,
        uploader: images[index].uploadedBy,
        startRect: spreadRect,
        startTransform,
        endRect,
        index,
      });
      console.log('[GRID] onFocusImage called:', {
        url: images[index].url,
        startRect: spreadRect,
        startTransform,
        endRect,
        index,
      });
    }
  };

  return (
    <div className={styles.gridWrapper}>
      <div ref={gridRef} className={styles.gridContainer}>
        {images.map((image, index) => (
          <GridImageTile
            key={image.id || index}
            image={image}
            index={index}
            focusedIndex={focusedIndex}
            inView={inViewArr[index] || false}
            onClick={handleImageClick}
            setInViewAtIndex={setInViewAtIndex}
            cancelFocus={cancelFocus}
          />
        ))}
      </div>
    </div>
  );
};

export default InteractiveImageGrid;